import React, { useState, useEffect } from 'react';
import { 
  Send, 
  CheckCircle2, 
  MapPin, 
  HardHat, 
  AlertTriangle, 
  Sliders, 
  FileText,
  Plus,
  Trash2,
  PackageCheck,
  Zap,
  AlertCircle
} from 'lucide-react';
import { Team, BuildingBlock, Floor, Zone, WorkType, Material } from '../../types';
import { PhotoUploader } from './PhotoUploader';

interface EveningReportWizardProps {
  onReportSubmitted?: () => void;
}

interface ReportWorkItemDraft {
  workTypeId: string;
  volumeCompleted: number;
  unit: string;
  materials: {
    materialId: string;
    calculatedVolume: number;
    actualVolume: number;
    deviationReason?: string;
    unit: string;
  }[];
  photos: string[];
}

export const EveningReportWizard: React.FC<EveningReportWizardProps> = ({ onReportSubmitted }) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [blocks, setBlocks] = useState<BuildingBlock[]>([]);
  const [floors, setFloors] = useState<Floor[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);
  const [materials, setMaterials] = useState<Material[]>([]);

  // Form State
  const [selectedTeamId, setSelectedTeamId] = useState<string>('team-01');
  const [foremanName, setForemanName] = useState<string>('Иванов И.И.');
  const [selectedBlockId, setSelectedBlockId] = useState<string>('block-a');
  const [selectedFloorId, setSelectedFloorId] = useState<string>('');
  const [selectedZoneId, setSelectedZoneId] = useState<string>('');
  const [zoneProgress, setZoneProgress] = useState<number>(60);
  const [notes, setNotes] = useState<string>('');

  // Work Items
  const [items, setItems] = useState<ReportWorkItemDraft[]>([
    {
      workTypeId: 'wt-cab-3x25',
      volumeCompleted: 240,
      unit: 'м',
      materials: [
        { materialId: 'mat-vvg-3x25', calculatedVolume: 252, actualVolume: 252, deviationReason: '', unit: 'м' }
      ],
      photos: []
    }
  ]);

  // Downtime
  const [hasDowntime, setHasDowntime] = useState<boolean>(false);
  const [downtimeCategory, setDowntimeCategory] = useState<string>('walls_not_plastered');
  const [downtimeHours, setDowntimeHours] = useState<number>(2);
  const [downtimeDesc, setDowntimeDesc] = useState<string>('Штукатуры не подготовили поверхность стен под штробление и подрозетники');

  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load Tree and Catalogs
  useEffect(() => {
    fetch('/api/v1/hr/teams')
      .then(r => r.json())
      .then(d => {
        if (d.success && d.data.length > 0) {
          setTeams(d.data);
          setSelectedTeamId(d.data[0].id);
          setForemanName(d.data[0].foremanName);
        }
      });

    fetch('/api/v1/projects/proj-sz-2026/tree')
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setBlocks(d.data.blocks);
          setFloors(d.data.floors);
          setZones(d.data.zones);
          if (d.data.floors.length > 0) setSelectedFloorId(d.data.floors[0].id);
          if (d.data.zones.length > 0) {
            setSelectedZoneId(d.data.zones[0].id);
            setZoneProgress(d.data.zones[0].completionPercentage || 50);
          }
        }
      });

    fetch('/api/v1/schemas/work-types')
      .then(r => r.json())
      .then(d => { if (d.success) setWorkTypes(d.data); });

    fetch('/api/v1/schemas/materials')
      .then(r => r.json())
      .then(d => { if (d.success) setMaterials(d.data); });
  }, []);

  const addWorkItem = () => {
    setItems([
      ...items,
      {
        workTypeId: workTypes[0]?.id || 'wt-cab-3x25',
        volumeCompleted: 50,
        unit: 'м',
        materials: [
          { materialId: materials[0]?.id || 'mat-vvg-3x25', calculatedVolume: 52.5, actualVolume: 52.5, deviationReason: '', unit: 'м' }
        ],
        photos: []
      }
    ]);
  };

  const removeWorkItem = (idx: number) => {
    setItems(items.filter((_, i) => i !== idx));
  };

  const updateWorkItemVolume = (idx: number, volume: number) => {
    const next = [...items];
    next[idx].volumeCompleted = volume;
    // Auto-calculate material with 1.05 norm (5% waste factor)
    next[idx].materials = next[idx].materials.map(m => {
      const calc = Number((volume * 1.05).toFixed(2));
      return {
        ...m,
        calculatedVolume: calc,
        actualVolume: calc
      };
    });
    setItems(next);
  };

  const updateActualMaterial = (itemIdx: number, matIdx: number, actual: number, reason?: string) => {
    const next = [...items];
    const mat = next[itemIdx].materials[matIdx];
    mat.actualVolume = actual;
    if (reason !== undefined) mat.deviationReason = reason;
    setItems(next);
  };

  const updateItemPhotos = (itemIdx: number, photos: string[]) => {
    const next = [...items];
    next[itemIdx].photos = photos;
    setItems(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // 1. Submit Evening Report
      const res = await fetch('/api/v1/foreman/reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teamId: selectedTeamId,
          foremanName,
          blockId: selectedBlockId,
          floorId: selectedFloorId,
          zoneId: selectedZoneId,
          items,
          zoneProgress,
          notes
        })
      });

      const data = await res.json();

      // 2. Optional Downtime Incident Submission
      if (hasDowntime && downtimeHours > 0) {
        await fetch('/api/v1/foreman/incidents', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            blockId: selectedBlockId,
            zoneId: selectedZoneId,
            reasonCategory: downtimeCategory,
            hoursLost: downtimeHours,
            description: downtimeDesc
          })
        });
      }

      if (data.success) {
        setSuccessMessage('✅ Вечерний рапорт и фотофиксация объемов успешно приняты системой!');
        if (onReportSubmitted) onReportSubmitted();
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        alert(`Ошибка при отправке рапорта: ${data.error}`);
      }
    } catch (err) {
      console.error(err);
      alert('Ошибка соединения с сервером.');
    } finally {
      setSubmitting(false);
    }
  };

  const currentZone = zones.find(z => z.id === selectedZoneId);

  return (
    <div className="bg-white border border-slate-200/90 rounded-2xl p-4 sm:p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="border-b border-slate-100 pb-4">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <span className="p-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-xl">
            <FileText className="w-5 h-5" />
          </span>
          <span>Вечерний рапорт выработки & Автосписание материалов (1.05)</span>
        </h2>
        <p className="text-xs text-slate-500 mt-1">
          Пошаговый мастер внесения выполненных объемов, расчета фактического расхода кабельной продукции и загрузки фото
        </p>
      </div>

      {successMessage && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-sm font-semibold flex items-center gap-3 animate-fade-in">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
          <span>{successMessage}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Step 1: Location & Team Details */}
        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4">
          <span className="text-xs font-bold text-slate-700 uppercase tracking-wider block">
            1. Локация и рабочее звено
          </span>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-slate-600 font-semibold block mb-1">Звено / Прораб</label>
              <select
                value={selectedTeamId}
                onChange={(e) => {
                  setSelectedTeamId(e.target.value);
                  const t = teams.find(team => team.id === e.target.value);
                  if (t) setForemanName(t.foremanName);
                }}
                className="w-full bg-white border border-slate-200 rounded-xl p-2 text-xs text-slate-800 focus:outline-none focus:border-blue-500 font-medium"
              >
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>

            <div>
              <label className="text-xs text-slate-600 font-semibold block mb-1">Корпус</label>
              <select
                value={selectedBlockId}
                onChange={(e) => setSelectedBlockId(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-xl p-2 text-xs text-slate-800 focus:outline-none focus:border-blue-500 font-medium"
              >
                {blocks.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>

            <div>
              <label className="text-xs text-slate-600 font-semibold block mb-1">Этаж</label>
              <select
                value={selectedFloorId}
                onChange={(e) => setSelectedFloorId(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-xl p-2 text-xs text-slate-800 focus:outline-none focus:border-blue-500 font-medium"
              >
                {floors.filter(f => f.blockId === selectedBlockId).map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs text-slate-600 font-semibold block mb-1">Зона / Захватка</label>
              <select
                value={selectedZoneId}
                onChange={(e) => {
                  setSelectedZoneId(e.target.value);
                  const z = zones.find(zn => zn.id === e.target.value);
                  if (z) setZoneProgress(z.completionPercentage || 60);
                }}
                className="w-full bg-white border border-slate-200 rounded-xl p-2 text-xs text-slate-800 focus:outline-none focus:border-blue-500 font-medium"
              >
                {zones.filter(z => z.blockId === selectedBlockId).map(z => (
                  <option key={z.id} value={z.id}>{z.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Step 2: Completed Work Volumes & Material Calculation */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
              2. Выполненные виды работ и списание материалов
            </span>
            <button
              type="button"
              onClick={addWorkItem}
              className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 hover:bg-blue-100 px-3 py-1.5 rounded-lg border border-blue-200 transition font-bold"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>+ Добавить вид работы</span>
            </button>
          </div>

          {items.map((item, idx) => (
            <div key={idx} className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-4 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200 pb-3">
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-2">
                    <label className="text-[11px] text-slate-500 font-semibold block mb-1">Наименование работы ЭОМ</label>
                    <select
                      value={item.workTypeId}
                      onChange={(e) => {
                        const next = [...items];
                        next[idx].workTypeId = e.target.value;
                        setItems(next);
                      }}
                      className="w-full bg-white border border-slate-200 rounded-xl p-2 text-xs text-slate-900 font-bold focus:outline-none focus:border-blue-500"
                    >
                      {workTypes.map(wt => <option key={wt.id} value={wt.id}>{wt.name} ({wt.unit})</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="text-[11px] text-slate-500 font-semibold block mb-1">Объем выполнения ({item.unit})</label>
                    <input
                      type="number"
                      value={item.volumeCompleted}
                      onChange={(e) => updateWorkItemVolume(idx, parseFloat(e.target.value) || 0)}
                      className="w-full bg-white border border-slate-200 rounded-xl p-2 text-xs text-slate-900 font-bold focus:outline-none focus:border-amber-500"
                    />
                  </div>
                </div>

                {items.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeWorkItem(idx)}
                    className="p-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-lg transition self-end sm:self-center"
                    title="Удалить позицию"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Material Calculation Cards */}
              <div className="space-y-2">
                <span className="text-[11px] text-slate-600 font-bold flex items-center gap-1.5">
                  <PackageCheck className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Расчет расхода материалов по норме 1.05 (отходы 5%):</span>
                </span>

                {item.materials.map((mat, mIdx) => {
                  const hasDeviation = mat.actualVolume !== mat.calculatedVolume;
                  return (
                    <div key={mIdx} className="bg-white p-3 rounded-xl border border-slate-200 text-xs space-y-2">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <span className="font-bold text-slate-900">
                          Кабель ВВГнг-LS 3х2.5 ({mat.unit})
                        </span>
                        <div className="flex items-center gap-3 text-[11px]">
                          <span className="text-slate-500">
                            План по норме: <strong className="text-slate-800">{mat.calculatedVolume} {mat.unit}</strong>
                          </span>
                          <div className="flex items-center gap-1">
                            <span className="text-slate-600 font-semibold">Факт:</span>
                            <input
                              type="number"
                              value={mat.actualVolume}
                              onChange={(e) => updateActualMaterial(idx, mIdx, parseFloat(e.target.value) || 0)}
                              className={`w-20 bg-slate-50 border rounded-lg px-2 py-1 font-extrabold text-xs focus:outline-none ${
                                hasDeviation ? 'border-amber-500 text-amber-700 bg-amber-50' : 'border-slate-300 text-slate-900'
                              }`}
                            />
                          </div>
                        </div>
                      </div>

                      {hasDeviation && (
                        <div className="pt-2 border-t border-slate-100 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                          <input
                            type="text"
                            placeholder="Укажите причину отклонения от нормы 1.05 (напр. обрезка на поворотах лотка)"
                            value={mat.deviationReason || ''}
                            onChange={(e) => updateActualMaterial(idx, mIdx, mat.actualVolume, e.target.value)}
                            className="w-full bg-amber-50/50 border border-amber-200 rounded-lg p-1.5 text-xs text-amber-900 placeholder:text-amber-600/70 focus:outline-none"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Photo Uploader */}
              <PhotoUploader
                photos={item.photos}
                onChange={(photos) => updateItemPhotos(idx, photos)}
                zoneName={currentZone?.name}
              />
            </div>
          ))}
        </div>

        {/* Step 3: Zone Readiness Progress Bar Slider */}
        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
          <div className="flex items-center justify-between text-xs">
            <span className="font-bold text-slate-700 flex items-center gap-1.5">
              <Sliders className="w-4 h-4 text-blue-600" />
              <span>Готовность стояка / зоны после рапорта:</span>
            </span>
            <span className="font-black text-blue-700 text-sm">{zoneProgress}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            step="5"
            value={zoneProgress}
            onChange={(e) => setZoneProgress(parseInt(e.target.value))}
            className="w-full accent-blue-600 cursor-pointer"
          />
        </div>

        {/* Step 4: Optional Downtime Incident Checkbox */}
        <div className="bg-rose-50/70 border border-rose-200 p-4 rounded-xl space-y-3">
          <label className="flex items-center space-x-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={hasDowntime}
              onChange={(e) => setHasDowntime(e.target.checked)}
              className="w-4 h-4 text-rose-600 rounded bg-white border-rose-300 focus:ring-0"
            />
            <span className="text-xs font-bold text-rose-900 flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4 text-rose-600" />
              <span>Зафиксировать факт простоя на смене (задержка смежников)</span>
            </span>
          </label>

          {hasDowntime && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 text-xs border-t border-rose-200">
              <div>
                <label className="text-slate-700 font-semibold block mb-1">Причина простоя</label>
                <select
                  value={downtimeCategory}
                  onChange={(e) => setDowntimeCategory(e.target.value)}
                  className="w-full bg-white border border-rose-200 rounded-lg p-2 text-slate-800 font-medium"
                >
                  <option value="walls_not_plastered">Стены не оштукатурены</option>
                  <option value="no_materials">Нет кабельной продукции</option>
                  <option value="flooding">Затопление / Нет света</option>
                  <option value="other">Иное</option>
                </select>
              </div>

              <div>
                <label className="text-slate-700 font-semibold block mb-1">Потеряно часов</label>
                <input
                  type="number"
                  value={downtimeHours}
                  onChange={(e) => setDowntimeHours(parseFloat(e.target.value) || 0)}
                  className="w-full bg-white border border-rose-200 rounded-lg p-2 text-slate-800 font-bold"
                />
              </div>

              <div>
                <label className="text-slate-700 font-semibold block mb-1">Описание ситуации</label>
                <input
                  type="text"
                  value={downtimeDesc}
                  onChange={(e) => setDowntimeDesc(e.target.value)}
                  className="w-full bg-white border border-rose-200 rounded-lg p-2 text-slate-800"
                />
              </div>
            </div>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white font-bold py-3.5 rounded-xl shadow-md shadow-blue-600/20 active:scale-[0.99] transition flex items-center justify-center gap-2 text-sm disabled:opacity-50"
        >
          <Send className="w-4 h-4" />
          <span>{submitting ? 'Отправка...' : 'Передать рапорт в ПТО & Обновить 2D-Шахматку'}</span>
        </button>
      </form>
    </div>
  );
};
